import { UploadDropzone } from '@atlas/ui-components'
import { Checkbox } from '@atlas/ui-primitives'
import { ChevronUp } from 'lucide-react'
import { ASSET_LIST_TILE_GRID } from '../asset-tile/asset-thumb-tile'
import type { AssetListRole } from './asset-list-card.types'
import { AssetListTile } from './asset-list-tile'

export interface AssetListGroupProps {
	role: AssetListRole
	expanded: boolean
	onToggleExpanded: (code: string) => void
	/**
	 * Whether the card is selecting. Also decides whether this group offers a select-all at
	 * all: without a download action there is nothing to select for.
	 */
	selectionMode: boolean
	/** Whether selection is on offer on this card, i.e. whether the host gave a download action. */
	selectable: boolean
	/** The card's whole selection, keyed by asset id - an asset listed under two roles is one entry. */
	selectedAssetIds: ReadonlySet<string>
	onToggleSelection: (assetId: string) => void
	/** Select or deselect exactly this group's assets; the card decides which way from the state below. */
	onToggleGroupSelection: (assetIds: string[], select: boolean) => void
	onOpenAsset: (assetId: string) => void
	/**
	 * Files were dropped on or picked for THIS group. Given only when the card's host offers
	 * an import; the group reports its own `code` and nothing else - what that code means is
	 * the host's question.
	 */
	onImportFiles?: (roleCode: string, files: File[]) => void
}

/**
 * One role as a collapsible group: its label and count stay readable while collapsed, so
 * the list's shape - which roles are supplied and how many of each - is legible without
 * expanding anything.
 *
 * The group keeps a real checkbox: it stands for one whole category, is the fastest way into
 * a selection, and there is one of it per role rather than one per asset - so it carries no
 * clutter and needs no hover to be found.
 *
 * The disclosure and the select-all are two sibling controls rather than one nested pair,
 * because a checkbox inside a button has no working keyboard semantics. Collapsing
 * unmounts the tiles, which is safe: the thumbnails are resolved by the host and arrive as
 * props, so an expansion re-renders from what is already in hand rather than re-waiting.
 *
 * A group holding NO assets still renders, with its label and a count of zero. A host
 * supplies its own roles, so an empty one is a statement that the role exists and is
 * unsupplied - which is a different thing from the role never having been asked about, and
 * is the group an import most often has to land on.
 */
export function AssetListGroup({
	role,
	expanded,
	onToggleExpanded,
	selectionMode,
	selectable,
	selectedAssetIds,
	onToggleSelection,
	onToggleGroupSelection,
	onOpenAsset,
	onImportFiles,
}: AssetListGroupProps) {
	const { code, label, assets } = role
	// An asset still uploading counts in the group's total, because it is a tile the reader can
	// see, but it is not something a select-all can reach: there is nothing stored to download.
	const storedAssets = assets.filter((asset) => asset.uploadProgress === undefined)
	const selectedCount = storedAssets.filter((asset) => selectedAssetIds.has(asset.id)).length
	const allSelected = selectedCount > 0 && selectedCount === storedAssets.length

	return (
		<section>
			<div className="mb-ds-3 flex items-center gap-ds-2">
				{/* An empty group offers no select-all: there is nothing to select. */}
				{selectable && storedAssets.length > 0 ? (
					<Checkbox
						size="sm"
						isSelected={allSelected}
						isIndeterminate={selectedCount > 0 && !allSelected}
						onChange={(select) =>
							onToggleGroupSelection(
								storedAssets.map((asset) => asset.id),
								select,
							)
						}
						aria-label={`Select all ${label}`}
					/>
				) : null}
				<button
					type="button"
					onClick={() => onToggleExpanded(code)}
					aria-expanded={expanded}
					className="group/shelf flex min-w-0 items-center gap-1.5 rounded-ds-sm outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
				>
					<h3 className="font-mono text-ds-2xs uppercase tracking-ds-wider text-ds-text-subtle transition-colors duration-(--ds-motion-duration-fast) group-hover/shelf:text-ds-text">
						{label} · {assets.length}
					</h3>
					<ChevronUp
						aria-hidden
						className={`size-3.5 text-ds-text-subtle transition-transform duration-(--ds-motion-duration-base) group-hover/shelf:text-ds-text ${expanded ? '' : 'rotate-180'}`}
					/>
				</button>
				{/* The shelf's rule, carrying the group's own state: solid where the role is
				    supplied, dashed where it is stated and empty. A run of groups then reads as a
				    shelf list whose gaps are visible as a shape rather than as a zero to notice. */}
				<span
					aria-hidden
					className={
						assets.length === 0
							? 'min-w-0 flex-1 border-t border-dashed border-ds-border'
							: 'h-px min-w-0 flex-1 bg-ds-border'
					}
				/>
			</div>
			{expanded && (assets.length > 0 || onImportFiles) ? (
				<div className={ASSET_LIST_TILE_GRID}>
					{assets.map((asset, position) => (
						<AssetListTile
							key={asset.id}
							index={position}
							asset={asset}
							roleLabel={label}
							selectionMode={selectionMode}
							selected={selectedAssetIds.has(asset.id)}
							onToggleSelection={onToggleSelection}
							onOpen={onOpenAsset}
						/>
					))}
					{/* The import belongs to the GROUP, not to a tile: an empty group has no tile to
					    hang it on, and that is the group most often imported into. It is the grid's
					    last cell rather than a bar under it, so it lines up with the assets it adds
					    to and stays inside the group it lands on - and a collapsed group takes it
					    with it. */}
					{onImportFiles ? (
						<UploadDropzone
							variant="tile"
							label={`Import into ${label}`}
							prompt="Import files"
							onFiles={(files) => onImportFiles(code, files)}
						/>
					) : null}
				</div>
			) : null}
		</section>
	)
}
